
/**
 * @typedef {Object} ShopifyCart
 * @property {number} item_count - Total number of items in cart
 * @property {number} total_price - Total price in cents
 * @property {Array<Object>} items - Array of cart line items
 */

/**
 * @typedef {Object} ShopifyCartResponse
 * @property {string} [status] - Error status if present
 * @property {string} [description] - Error description
 * @property {string} [message] - Error message
 * @property {Object} [errors] - Error details
 * @property {Object.<string, string>} [sections] - Updated section HTML
 * @property {number} [item_count] - Updated cart item count
 */

/**
 * Custom element for handling product form submissions and cart operations.
 * Manages add-to-cart functionality, cart count updates, and mini cart interactions.
 *
 * @extends {HTMLElement}
 */
if (!customElements.get('product-form')) {
	customElements.define('product-form', class ProductForm extends HTMLElement {
		/**
		 * Creates a new ProductForm instance.
		 * Initializes form references and sets up submit event listener.
		 */
		constructor() {
			super();

			/** @type {HTMLFormElement} */
			this.form = this.querySelector('form');
			this.form.querySelector('[name=id]').disabled = false;
			this.form.addEventListener('submit', this.onSubmitHandler.bind(this));

			/** @type {HTMLElement|null} */
			this.minicart = document.querySelector('mini-cart');

			/** @type {string} */
			this.eventContext = this.form.dataset.eventContext;

			/** @type {boolean} */
			this.hideErrors = this.dataset.hideErrors === 'true';

			/** @type {HTMLElement|null} */
			this.errorMessageWrapper = null;

			/** @type {HTMLElement|null} */
			this.errorMessage = null;
		}

		/**
		 * Updates cart count display across all cart count elements in the page.
		 * Fetches current cart state and updates count badges, aria labels, and visibility.
		 *
		 * @returns {Promise<void>}
		 */
		updateCartCount() {
			// Fetch current cart state and update the count
			fetch('/cart.js')
				.then(response => response.json())
				.then(cart => {
					// Update cart count in header - try multiple selectors
					const cartCountSelectors = [
						'.head-slot-cart-link-quantity',
						'.cart-link-count',
						'.cart-count-bubble',
						'.js-cart-count',
						'.site-header__cart-count',
						'.header__cart-count',
						'[data-cart-count]',
						'.head-slot-cart-link .badge',
						'.cart-link .badge'
					];

					// Special handling for head-slot-cart-link-quantity
					const cartLink = document.querySelector('.head-slot-cart-link');
					if (cartLink && cart.item_count > 0) {
						let quantityElement = cartLink.querySelector('.head-slot-cart-link-quantity');
						if (!quantityElement) {
							// Create the quantity element if it doesn't exist
							quantityElement = document.createElement('span');
							quantityElement.className = 'head-slot-cart-link-quantity';
							cartLink.appendChild(quantityElement);
						}
						quantityElement.textContent = cart.item_count;
					} else if (cartLink && cart.item_count === 0) {
						// Remove quantity element when cart is empty
						const quantityElement = cartLink.querySelector('.head-slot-cart-link-quantity');
						if (quantityElement) {
							quantityElement.remove();
						}
					}

					// Update other cart count selectors
					cartCountSelectors.forEach(selector => {
						const elements = document.querySelectorAll(selector);
						elements.forEach(countElement => {
							if (countElement) {
								// Handle different element types
								if (countElement.hasAttribute('data-cart-count')) {
									countElement.setAttribute('data-cart-count', cart.item_count);
								}

								// Update text content
								countElement.textContent = cart.item_count;

								// Show/hide based on count
								if (cart.item_count > 0) {
									countElement.classList.remove('hidden', 'hide', 'visually-hidden');
									countElement.style.display = '';
								} else if (cart.item_count === 0 && !countElement.classList.contains('head-slot-cart-link-quantity')) {
									countElement.classList.add('visually-hidden');
								}
							}
						});
					});

					// Also update any cart links with the count in aria-label
					const cartLinks = document.querySelectorAll('a[href="/cart"]');
					cartLinks.forEach(link => {
						if (link.hasAttribute('aria-label')) {
							link.setAttribute('aria-label', `Cart with ${cart.item_count} items`);
						}
					});
				})
				.catch(error => console.error('Error updating cart count:', error));
		}

		/**
		 * Handles form submission for adding products to cart.
		 * Prevents default form submission, shows loading state, and uses fetch API.
		 *
		 * @param {Event} event - The form submit event
		 * @returns {void}
		 */
		onSubmitHandler(event) {
			event.preventDefault();
			const submitButton = this.querySelector('[type="submit"]');
			if (submitButton.classList.contains('loading')) {
				return;
			}

			this.handleErrorMessage();

			submitButton.setAttribute('aria-disabled', true);
			submitButton.classList.add('loading');
			this.querySelector('.button-overlay-spinner').classList.remove('hidden');

			const config = fetchConfig('javascript');
			config.headers['X-Requested-With'] = 'XMLHttpRequest';
			delete config.headers['Content-Type'];

			const formData = new FormData(this.form);
			// Get sections to update - only mini cart sections
			const sectionsToUpdate = this.minicart && typeof this.minicart.getSectionsToRender === 'function'
				? this.minicart.getSectionsToRender().map((section) => section.id)
				: ['header-mini-cart-content', 'header-mini-cart-footer'];

			formData.append('sections', sectionsToUpdate.join(','));
			formData.append('sections_url', window.location.pathname);
			config.body = formData;

			fetch(`${routes.cart_add_url}`, config)
				.then((response) => response.json())
				.then((response) => {
					if (response.status) {
						publish(PUB_SUB_EVENTS.cartError, {
							source: 'product-form',
							productVariantId: formData.get('id'),
							errors: response.errors || response.description,
							message: response.message,
						});

						this.handleErrorMessage(response.description);
						if (this.minicart && typeof this.minicart.updateSectionContents === 'function') {
							this.minicart.updateSectionContents();
						}
						return;
					}

					// Always update cart count first
					this.updateCartCount();

					// Then try to render mini cart contents if available
					if (this.minicart && typeof this.minicart.renderContents === 'function') {
						this.minicart.renderContents(response);
					}

					if (window.themeSettings.redirectToCart) {
						location.href = '/cart';
					} else if (this.minicart && typeof this.minicart.open === 'function') {
						this.minicart.open(submitButton);
					}

					const event = new CustomEvent('cart:item-add', {
						detail: {
							items: [response],
							context: this.eventContext,
						},
					});
					document.dispatchEvent(event);

					publish(PUB_SUB_EVENTS.cartItemAdd, {
						source: this.eventContext,
						items: [response],
					});
				})
				.catch((e) => {
					console.error(e);
				})
				.finally(() => {
					submitButton.classList.remove('loading');
					submitButton.removeAttribute('aria-disabled');
					this.querySelector('.button-overlay-spinner').classList.add('hidden');
				});
		}

		/**
		 * Displays or hides error messages for the product form.
		 * Manages error wrapper visibility and message content.
		 *
		 * @param {string|boolean} [errorMessage=false] - The error message to display, or false to hide errors
		 * @returns {void}
		 */
		handleErrorMessage(errorMessage = false) {
			if (this.hideErrors) {
				return;
			}

			this.errorMessageWrapper = this.errorMessageWrapper || this.querySelector('.product-form-error-message-wrapper');
			this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form-error-message');

			this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

			if (errorMessage) {
				this.errorMessage.textContent = errorMessage;
			}
		}
	});
}
